import { ThemeProvider } from '@emotion/react';
import is, { assert } from '@sindresorhus/is';
import type { FunctionComponent, PropsWithChildren } from 'react';
import { createContext, useContext, useEffect } from 'react';
import { Navigate, useParams } from 'react-router';
import { Maybe } from 'true-myth';
import { z } from 'zod';

import { ApplicationContext } from '~/shared/context/context';
import { theme } from '~/ui/theme/cobrainer-theme';

import { useUserSettingsUiMutation } from '../../app/query-hooks/use-user-settings-ui';
import { useJobArchitectures } from '../api/use-job-architectures';
import { OnboardingChangesetProvider } from '../onboarding/OnboardingChangesetProvider';

const JobArchitectureScopeContext = createContext<string | null>(null);

// Provides the architecture id a subtree operates on, together with the
// per-architecture provider stack (theme + onboarding changeset). Each host —
// the route page (id from the route param), every URL tile render (id from the
// tile payload), and the standalone profile page (id from its own route) — gets
// its own stack keyed to ITS architecture. This is what lets several JSA tiles
// for different architectures coexist (e.g. the dashboard) without a single
// shared wrapper deriving one ambient architecture.
//
// The draft-version store is deliberately NOT here: it is keyed by item id (not
// by architecture) and must be SHARED across sibling tiles so an edit made in
// the job-profile tile is visible in the compare tile. It is provided once per
// tile surface by JobArchitectureUrlTilesProvider (the JSA url-tile module
// Provider).
export const JobArchitectureScopeProvider: FunctionComponent<
    PropsWithChildren<{ readonly architectureId: string }>
> = ({ architectureId, children }) => (
    <JobArchitectureScopeContext.Provider value={architectureId}>
        <ThemeProvider theme={theme}>
            <OnboardingChangesetProvider architectureId={architectureId}>{children}</OnboardingChangesetProvider>
        </ThemeProvider>
    </JobArchitectureScopeContext.Provider>
);

export const useActiveJobArchitectureId = (): string => {
    const architectureId = useContext(JobArchitectureScopeContext);
    assert.nonEmptyString(architectureId);

    return architectureId;
};

const isEmptyOrUndefined = (id: string | undefined): boolean => {
    return is.undefined(id) || id === '';
};

export const useValidatedJobArchitectureId = (): string | undefined => {
    const params = useParams<{ architectureId?: string; id?: string }>();
    const id = params.architectureId ?? params.id;

    const jobArchitectures = useJobArchitectures();

    if (isEmptyOrUndefined(id)) {
        return undefined;
    }

    const validationResult = z.uuid().safeParse(id);
    if (!validationResult.success) {
        return undefined;
    }

    if (jobArchitectures.isSuccess) {
        const architectureExists = jobArchitectures.data?.some((arch) => arch.id === id) ?? false;
        if (!architectureExists) {
            return undefined;
        }
    }

    return id;
};

export const useRememberSelectedJobArchitecture = (validJobArchitectureId: string | undefined): void => {
    const {
        applicationState: { uiSettings },
        validatingCobrainerApiFetcher,
    } = useContext(ApplicationContext);
    const {
        userSettingsUiMutation: { mutate },
    } = useUserSettingsUiMutation(validatingCobrainerApiFetcher);

    useEffect(() => {
        if (is.undefined(validJobArchitectureId)) {
            return;
        }

        if (
            uiSettings.isJust &&
            !uiSettings.get('selectedJobArchitectureId').equals(Maybe.of(validJobArchitectureId))
        ) {
            mutate({ selectedJobArchitectureId: validJobArchitectureId });
        }
    }, [validJobArchitectureId, mutate, uiSettings]);
};

// Route-level host: validates the :id route param, redirects when invalid, and
// remembers the visited architecture in the user's UI settings.
export const JobArchitectureRouteScope: FunctionComponent<PropsWithChildren<{ readonly redirectTo?: string }>> = ({
    children,
    redirectTo = '/job-architecture-v2',
}) => {
    const validJobArchitectureId = useValidatedJobArchitectureId();
    useRememberSelectedJobArchitecture(validJobArchitectureId);

    if (is.undefined(validJobArchitectureId)) {
        return <Navigate to={redirectTo} />;
    }

    return (
        <JobArchitectureScopeProvider architectureId={validJobArchitectureId}>{children}</JobArchitectureScopeProvider>
    );
};
